import { useNavigate } from 'react-router-dom';
import { BookOpen, Disc3, Download, FileX2, Heart, HeartOff, ListEnd, ListPlus, ListStart, Music4, PencilLine, Radio, Trash2, User } from 'lucide-react';
import { useAccess } from '../lib/access';
import type { Album, DeleteScope, Track } from '@pujolisique/shared';
import { downloadUrl } from '../api/client';
import { fetchAlbum, fetchMix, useFavoriteToggle } from '../api/hooks';
import { usePlayer } from '../player/store';
import { useUi, type DeleteRequest, type MenuItem } from '../ui/store';
import { toast } from '../ui/toast';
import { plural } from '../lib/format';

export function useStartMix() {
  const playTracks = usePlayer((s) => s.playTracks);
  return async (kind: string, id: string, label: string) => {
    try {
      const tracks = await fetchMix(kind, id);
      if (!tracks.length) return toast('Pas assez de titres pour un mix');
      playTracks(tracks, 0, { shuffle: false, source: `Mix ${label}` });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
}

/** Bouton « Supprimer » des pages album, série, dossier (administrateurs seulement) */
export function DeleteButton({ scope, label, onDone, text = 'Supprimer' }: { scope: DeleteScope; label: string; onDone?: () => void; text?: string }) {
  const { isAdmin } = useAccess();
  const askDelete = useUi((s) => s.askDelete);
  if (!isAdmin) return null;
  return (
    <button className="btn btn--ghost btn--danger" onClick={() => askDelete({ scope, label, onDone })} title="Effacer définitivement les fichiers du serveur">
      <FileX2 size={16} /> {text}
    </button>
  );
}

const deleteItem = (askDelete: (r: DeleteRequest) => void, req: DeleteRequest, label: string): MenuItem => ({
  label,
  icon: <FileX2 size={16} />,
  onSelect: () => askDelete(req),
  danger: true,
});

export function useTrackMenu() {
  const nav = useNavigate();
  const { canUpload, isAdmin } = useAccess();
  const askDelete = useUi((s) => s.askDelete);
  const { playNext, addToQueue } = usePlayer.getState();
  const openMenu = useUi((s) => s.openMenu);
  const pickPlaylist = useUi((s) => s.pickPlaylist);
  const fav = useFavoriteToggle();
  const mix = useStartMix();

  return (track: Track, e: { clientX: number; clientY: number } | DOMRect, opts: { onRemove?: () => void; removeLabel?: string } = {}) => {
    const items: MenuItem[] = [
      { label: 'Lire ensuite', icon: <ListStart size={16} />, onSelect: () => (playNext([track]), toast('Lu juste après le titre en cours')) },
      { label: 'Ajouter à la file', icon: <ListEnd size={16} />, onSelect: () => (addToQueue([track]), toast('Ajouté à la file d’attente')) },
      { label: 'Ajouter à une playlist…', icon: <ListPlus size={16} />, onSelect: () => pickPlaylist([track]) },
      ...(track.kind === 'music' ? [{ label: 'Lancer un mix à partir de ce titre', icon: <Radio size={16} />, onSelect: () => mix('track', track.id, track.title) }] : []),
      {
        label: track.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris',
        icon: track.favorite ? <HeartOff size={16} /> : <Heart size={16} />,
        onSelect: () => fav.mutate({ kind: 'track', id: track.id, value: !track.favorite }, { onSuccess: () => toast(track.favorite ? 'Retiré des favoris' : 'Ajouté aux favoris') }),
      },
    ];
    if (track.kind === 'stories' && track.folderId) items.push({ label: 'Voir la série', icon: <BookOpen size={16} />, onSelect: () => nav(`/stories/${track.folderId}`) });
    if (track.kind === 'music' && track.albumId) items.push({ label: 'Voir l’album', icon: <Disc3 size={16} />, onSelect: () => nav(`/album/${track.albumId}`) });
    if (track.kind === 'music') for (const a of track.artists.slice(0, 2)) if (a.id) items.push({ label: `Voir ${a.name}`, icon: <User size={16} />, onSelect: () => nav(`/artist/${a.id}`) });
    if (track.kind === 'music') for (const c of track.composers.slice(0, 2))
      if (!c.id.startsWith('name:')) items.push({ label: `Compositeur : ${c.name}`, icon: <Music4 size={16} />, onSelect: () => nav(`/composer/${c.id}`) });
    items.push({
      label: 'Télécharger le fichier',
      icon: <Download size={16} />,
      onSelect: () => {
        const a = document.createElement('a');
        a.href = downloadUrl(track.id);
        a.download = '';
        a.click();
      },
    });
    if (canUpload) items.push({ label: 'Modifier les informations…', icon: <PencilLine size={16} />, onSelect: () => nav(`/edit/track/${track.id}`) });
    if (opts.onRemove) items.push({ label: opts.removeLabel ?? 'Retirer de la playlist', icon: <Trash2 size={16} />, onSelect: opts.onRemove, danger: true });
    if (isAdmin) items.push(deleteItem(askDelete, { scope: { tracks: [track.id] }, label: `« ${track.title} »` }, 'Supprimer le fichier…'));
    openMenu(e, items, track.title);
  };
}

export function useAlbumMenu() {
  const nav = useNavigate();
  const { canUpload, isAdmin } = useAccess();
  const askDelete = useUi((s) => s.askDelete);
  const openMenu = useUi((s) => s.openMenu);
  const pickPlaylist = useUi((s) => s.pickPlaylist);
  const fav = useFavoriteToggle();
  const mix = useStartMix();
  return (album: Album, e: { clientX: number; clientY: number } | DOMRect) => {
    const withTracks = async (fn: (t: Track[]) => void) => {
      try {
        fn((await fetchAlbum(album.id)).tracks);
      } catch (err) {
        toast((err as Error).message, 'error');
      }
    };
    openMenu(
      e,
      [
        { label: 'Lire ensuite', icon: <ListStart size={16} />, onSelect: () => withTracks((t) => (usePlayer.getState().playNext(t), toast(`${plural(t.length, 'titre', 'titres')} lus ensuite`))) },
        { label: 'Ajouter à la file', icon: <ListEnd size={16} />, onSelect: () => withTracks((t) => (usePlayer.getState().addToQueue(t), toast('Album ajouté à la file'))) },
        { label: 'Ajouter à une playlist…', icon: <ListPlus size={16} />, onSelect: () => withTracks((t) => pickPlaylist(t)) },
        { label: 'Lancer un mix', icon: <Radio size={16} />, onSelect: () => mix('album', album.id, album.name) },
        {
          label: album.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris',
          icon: album.favorite ? <HeartOff size={16} /> : <Heart size={16} />,
          onSelect: () => fav.mutate({ kind: 'album', id: album.id, value: !album.favorite }),
        },
        ...(canUpload ? [{ label: 'Modifier l’album…', icon: <PencilLine size={16} />, onSelect: () => nav(`/edit/album/${album.id}`) }] : []),
        ...(isAdmin ? [deleteItem(askDelete, { scope: { album: album.id }, label: `l’album « ${album.name} »` }, 'Supprimer l’album…')] : []),
      ],
      album.name,
    );
  };
}
